import { normalizeRoutePath } from "../../lib/chat-navigation";
import type { RouteIntro } from "./route-context-intros";

/**
 * Employer-workspace route intros (Employer Phase 1).
 *
 * The seeker catalog in `route-context-intros.ts` is keyed by
 * `PageContextScreen` and localized in the seeker namespace — both strictly
 * seeker-shaped. Employer surfaces have no ids in that union yet, so this
 * sibling catalog is keyed by its own route-derived keys and its `messageKeys`
 * / `titleKey`s resolve against the EMPLOYER namespace. The chat column
 * merges the two at the consumer (`thread.web.tsx` picks this catalog when
 * the active workspace is a company).
 */

export type EmployerRouteIntroKey = "applications" | "company-page";

const EMPLOYER_ROUTE_INTROS: Record<EmployerRouteIntroKey, RouteIntro> = {
  applications: {
    messageKeys: [
      "chat.routeIntro.applications.m1",
      "chat.routeIntro.applications.m2",
      "chat.routeIntro.applications.m3",
    ],
    chips: [
      {
        id: "applications.latest",
        titleKey: "chat.routeIntro.applications.chips.latest",
        prompt: "Show me the newest applications for our roles",
      },
      {
        id: "applications.pipeline",
        titleKey: "chat.routeIntro.applications.chips.pipeline",
        prompt: "Summarize our hiring pipeline by status",
      },
      {
        id: "applications.role",
        titleKey: "chat.routeIntro.applications.chips.role",
        prompt: "Which of our open roles is getting the most applicants?",
      },
    ],
  },

  "company-page": {
    messageKeys: [
      "chat.routeIntro.companyPage.m1",
      "chat.routeIntro.companyPage.m2",
      "chat.routeIntro.companyPage.m3",
    ],
    chips: [
      {
        id: "companyPage.improve",
        titleKey: "chat.routeIntro.companyPage.chips.improve",
        prompt: "Review our company page and suggest what to improve",
      },
      {
        id: "companyPage.bio",
        titleKey: "chat.routeIntro.companyPage.chips.bio",
        prompt: "Help me sharpen our one-line company bio",
      },
      {
        id: "companyPage.jobs",
        titleKey: "chat.routeIntro.companyPage.chips.jobs",
        prompt: "Help me draft a new job post for our careers page",
      },
    ],
  },
};

/** Maps the live pathname to an employer intro key (null = generic pills). */
export function getEmployerRouteIntroKey(
  pathname: string | null | undefined,
): EmployerRouteIntroKey | null {
  const path = normalizeRoutePath(pathname);
  if (path === "/applications" || path.startsWith("/applications/")) {
    return "applications";
  }
  if (path === "/company" || path.startsWith("/company/")) {
    return "company-page";
  }
  return null;
}

export function getEmployerRouteIntro(
  key: EmployerRouteIntroKey,
): RouteIntro {
  return EMPLOYER_ROUTE_INTROS[key];
}
